import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { savePrompt } from '../../services/dataService';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabase';

interface Prompt {
  id: string;
  name: string;
  content: string;
  feature: string;
  updated_at: string;
  private: boolean;
  general: boolean;
  domain_id: string | null;
}

interface SavePromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  promptContent: string;
  companyId: string;
  domainId: string | null;
  feature: string;
  existingPrompt?: Prompt;
  onSaved?: () => void;
}

type PromptScope = 'personal' | 'domain' | 'company';

export const SavePromptDialog = ({
  isOpen,
  onClose,
  promptContent,
  companyId,
  domainId,
  feature,
  existingPrompt,
  onSaved,
}: SavePromptDialogProps) => {
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [promptScope, setPromptScope] = useState<PromptScope>('personal');
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { user, userDetails } = useAuth();

  useEffect(() => {
    if (existingPrompt) {
      setName(existingPrompt.name);
      setContent(existingPrompt.content);
      // Determine the scope of the existing prompt
      if (existingPrompt.private) {
        setPromptScope('personal');
      } else if (existingPrompt.domain_id) {
        setPromptScope('domain');
      } else {
        setPromptScope('company');
      }
    } else {
      setName('');
      setContent(promptContent);
      setPromptScope('personal');
    }
  }, [existingPrompt, promptContent, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      showNotification(t('common.name_required'), 'error');
      return;
    }

    setIsLoading(true);

    try {
      // Determine prompt settings based on scope
      const isPrivate = promptScope === 'personal';
      const targetDomainId = promptScope === 'domain' ? domainId : null;

      if (existingPrompt) {
        // Update existing prompt
        const { error } = await supabase
          .from('prompts')
          .update({
            name,
            content: content,
            updated_at: new Date().toISOString(),
            private: isPrivate,
            domain_id: targetDomainId,
          })
          .eq('id', existingPrompt.id);

        if (error) throw error;
        showNotification(t('common.prompt_updated'), 'success');
      } else {
        // Create new prompt
        await savePrompt({
          name,
          content: content,
          feature,
          companyId,
          domainId: targetDomainId || undefined,
          private: isPrivate,
          userId: user?.id,
          creatorDetails: userDetails || {},
        });
        showNotification(t('common.prompt_saved'), 'success');
      }

      if (onSaved) {
        onSaved();
      }
      onClose();
    } catch (error) {
      console.error('Error saving prompt:', error);
      showNotification(
        error instanceof Error ? error.message : t('common.error_occurred'),
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[1300]">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-lg p-6 max-w-md w-full">
          <Dialog.Title className="text-lg font-medium mb-4">
            {existingPrompt ? t('common.edit_prompt') : t('common.save_prompt')}
          </Dialog.Title>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="prompt-name" className="block text-sm font-medium text-gray-700">
                {t('common.prompt_name')}
              </label>
              <input
                id="prompt-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                required
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="prompt-content" className="block text-sm font-medium text-gray-700">
                {t('common.prompt_content')}
              </label>
              <textarea
                id="prompt-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                rows={5}
                required
              />
            </div>

            <div>
              <fieldset className="mt-4">
                <legend className="text-sm font-medium text-gray-700 mb-2">
                  {t('common.prompt_scope')}
                </legend>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      id="personal-scope"
                      name="prompt-scope"
                      type="radio"
                      checked={promptScope === 'personal'}
                      onChange={() => setPromptScope('personal')}
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label
                      htmlFor="personal-scope"
                      className="ml-3 block text-sm font-medium text-gray-700"
                    >
                      {t('common.personal_prompt')}
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      id="domain-scope"
                      name="prompt-scope"
                      type="radio"
                      checked={promptScope === 'domain'}
                      onChange={() => setPromptScope('domain')}
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={!domainId}
                    />
                    <label
                      htmlFor="domain-scope"
                      className="ml-3 block text-sm font-medium text-gray-700"
                    >
                      {t('common.domain_prompt')}
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      id="company-scope"
                      name="prompt-scope"
                      type="radio"
                      checked={promptScope === 'company'}
                      onChange={() => setPromptScope('company')}
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label
                      htmlFor="company-scope"
                      className="ml-3 block text-sm font-medium text-gray-700"
                    >
                      {t('common.company_prompt')}
                    </label>
                  </div>
                </div>
              </fieldset>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
                disabled={isLoading}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm text-white rounded flex items-center bg-blue-600 hover:bg-blue-700"
                disabled={isLoading}
              >
                {isLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                {existingPrompt ? t('common.update') : t('common.save')}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default SavePromptDialog;
